import Navbar from './Navbar';
import airlineDiagram from '../images/airline-architecture.png';

const partGroups = [
    {
        title: 'Entry point',
        parts: [
            { name: 'React frontend', detail: 'The traveler and admin interface for signing in, searching flights, and booking.' },
            { name: 'API Gateway', detail: "Built with Spring Cloud Gateway and the only public port. It verifies each request's JWT and routes it to the right service by URL path." },
            { name: 'Redis', detail: 'Keeps a list of logged-out JWTs that the gateway checks before letting a request through, and doubles as a cache.' }
        ]
    },
    {
        title: 'Spring Boot microservices',
        note: 'Each service except Notification has its own MySQL database.',
        parts: [
            { name: 'Airline core', detail: 'Airlines and their aircraft.' },
            { name: 'Location', detail: 'Cities and airports.' },
            { name: 'User', detail: 'Accounts and login.' },
            { name: 'Ancillary', detail: 'Extras like meals and add-ons.' },
            { name: 'Flight ops', detail: 'Flight schedules and search.' },
            { name: 'Pricing', detail: 'Fares and fare rules. It gives Flight ops the lowest fares.' },
            { name: 'Booking', detail: 'Bookings and tickets.' },
            { name: 'Payment', detail: 'Payment records and the Razorpay checkout.' },
            { name: 'Seat', detail: 'Seat availability, updated when a booking is confirmed.' },
            { name: 'Notification', detail: 'Sends booking confirmations by email and SMS.' }
        ]
    }
];

const Airline = () => (
    <div className="inner-page">
        <Navbar />
        <main>
            <header className="inner-page-hero project-page-hero airline-page-hero">
                <div className="container">
                    <h1>Airline Booking Platform</h1>
                    <p className="inner-page-intro">
                        A flight booking system I'm building with Spring Boot microservices. Travelers can sign in, search flights, and book seats, while each service owns its own data and the services coordinate through REST calls and Kafka events. It's still a work-in-progress, so for now this page just shows the architecture. You can check out the repo below.
                    </p>
                </div>
            </header>

            <section className="page-section airline-page-section" aria-labelledby="airline-flow-heading">
                <div className="container">
                    <figure className="airline-diagram">
                        <img
                            src={airlineDiagram}
                            width="1546"
                            height="784"
                            alt="Architecture diagram of the airline booking platform. A React frontend calls an API Gateway, which checks Redis for revoked tokens, finds services through Eureka, and routes requests to Spring Boot microservices for users, flights, pricing, bookings, payments, seats, and notifications. Services exchange events through Apache Kafka, load configuration from a Config Server backed by a GitHub repo, take payments through Razorpay, and send confirmations through Gmail and Twilio."
                        />
                        <div className="airline-code-link">
                            <a
                                className="btn btn-primary"
                                href="https://github.com/ShauryaKala/MicroservicesPractice"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <i className="fab fa-github" aria-hidden="true"></i>
                                Check out the code here
                            </a>
                        </div>
                    </figure>

                    <div className="airline-explainer">
                        <h2 id="airline-flow-heading">How a booking works</h2>
                        <ol className="airline-flow">
                            <li>The login goes through the API Gateway to the User service.</li>
                            <li>Flight ops finds matching flights and asks Pricing for the lowest fares.</li>
                            <li>The Booking service creates the booking and ticket.</li>
                            <li>Booking asks the Payment service to collect payment.</li>
                            <li>Payment opens Razorpay's hosted checkout.</li>
                            <li>Razorpay confirms the payment back to the Payment service.</li>
                            <li>Payment publishes a <code>payment.completed</code> event to Kafka, and Booking picks it up.</li>
                            <li>Booking publishes <code>booking.confirmed</code>. Seat updates availability and Notification prepares the confirmation.</li>
                            <li>Notification emails and texts the traveler through Gmail and Twilio.</li>
                        </ol>

                        <h2>What each part does</h2>
                        {partGroups.map((group) => (
                            <div className="airline-part-group" key={group.title}>
                                <h3>{group.title}</h3>
                                {group.note && <p className="airline-part-note">{group.note}</p>}
                                <dl className={`airline-parts${group.parts.length === 3 ? ' airline-parts-thirds' : ''}`}>
                                    {group.parts.map((part) => (
                                        <div key={part.name}>
                                            <dt>{part.name}</dt>
                                            <dd>{part.detail}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    </div>
);

export default Airline;
